import { useEffect, useRef, useCallback, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import useAuth from './useAuth.js';
import { useAppAuthSafe } from './AppAuthContext.js';
import { tokenService, TOKEN_KEY } from './tokenService.js';
import { markGlobalLogout } from './globalLogout.js';
import { consumeHandoff } from './tokenHandoff.js';
import { clearCachedProfile } from './profileCache.js';

/**
 * How long before expiry the backend JWT is renewed.
 *
 * The token lives 15 minutes, so this leaves 13 minutes of quiet and two of
 * margin. The margin is also the window in which a returning tab renews
 * immediately rather than trusting the timer — see `handleRegain`.
 */
const REFRESH_BUFFER_MS = 2 * 60 * 1000;

/** Retries for a renewal that failed for a reason that might not repeat. */
const RENEW_RETRY_DELAYS_MS = [3_000, 10_000, 30_000];

/**
 * Auth0 errors that mean the session is genuinely gone and no amount of
 * retrying will bring it back — the user has to log in again.
 *
 * Everything else (a network blip, a throttled call, a 5xx from our own
 * token endpoint) is transient, and treating it as a logout is how a lost
 * packet turns into a full-page redirect through Auth0 and back.
 */
const SESSION_GONE_ERRORS = new Set([
  'login_required',
  'consent_required',
  'interaction_required',
  'missing_refresh_token',
]);

const isSessionGone = (err) =>
  SESSION_GONE_ERRORS.has(err?.error) || err?.name === 'MissingRefreshTokenError';

/** Auth0 SPA SDK cache entries (see @auth0/auth0-spa-js LocalStorageCache). */
const AUTH0_LOCALSTORAGE_PREFIX = '@@auth0spajs@@';

/**
 * Removes auth-related localStorage only so UI preferences (theme, language, etc.) survive.
 */
function clearAuthLocalStorage() {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem('user_uuid');
    localStorage.removeItem('workspace_uuid');
    clearCachedProfile();
    const toRemove = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key?.startsWith(AUTH0_LOCALSTORAGE_PREFIX)) {
        toRemove.push(key);
      }
    }
    toRemove.forEach((key) => localStorage.removeItem(key));
  } catch (e) {
    console.error('[clearAuthAndRedirect] Failed to clear auth storage:', e);
  }
}

/**
 * Clears auth storage (not the entire origin localStorage), writes the
 * cross-subdomain "logout everywhere" marker (see globalLogout.js — this is
 * what makes a logout on one {slug}.{rootDomain} also invalidate the
 * session on every other subdomain and the root domain, Atlassian/Jira-
 * style, instead of only the one you logged out from), and redirects to
 * Auth0 logout (or /login as fallback).
 *
 * `returnTo` is always the app's fixed base URL (VITE_APP_BASE_URL), never
 * the current {slug}.{rootDomain} origin. Auth0 only allows redirecting back
 * to URLs on its "Allowed Logout URLs" list — a wildcard entry there is
 * needed for every workspace subdomain otherwise (the same class of
 * mismatch as the Callback URL wildcard, just for logout). Funneling every
 * logout through one known URL means that list only ever needs the single
 * fixed entry, regardless of which subdomain the user logged out from.
 */
export const clearAuthAndRedirect = () => {
  clearAuthLocalStorage();
  markGlobalLogout();
  const domain = import.meta.env.VITE_AUTH0_DOMAIN;
  const clientId = import.meta.env.VITE_AUTH0_CLIENT_ID;
  const returnTo = encodeURIComponent(
    import.meta.env.VITE_APP_BASE_URL || window.location.origin,
  );
  if (domain && clientId) {
    window.location.href = `https://${domain}/v2/logout?client_id=${clientId}&returnTo=${returnTo}`;
  } else {
    window.location.href = '/login';
  }
};

/**
 * Bridges identity with the backend token exchange.
 *
 * Auth0 is only ever consulted for the ONE-TIME identity login on a
 * browser. Moving between {slug}.{rootDomain} workspace subdomains
 * afterwards — first landing after login, or an explicit workspace switch —
 * hands our own backend JWT directly to the new origin via a one-time URL
 * fragment instead of re-running Auth0 there (see tokenHandoff.js and
 * WorkspaceGate in apps/core-shell). This component:
 *
 * 1. Consumes a pending handoff, if present, on the very first render —
 *    before any descendant (e.g. ProtectedRoute) evaluates useAuth() on
 *    this same render pass.
 * 2. Otherwise, when Auth0 itself reports an authenticated session (true
 *    only on the origin where the identity login actually completed, or in
 *    embedded mode where the Shell's own ambient Auth0Provider already is),
 *    exchanges the Auth0 token for a backend one via POST /v1/token.
 * 3. Schedules proactive refresh of the backend token before expiry —
 *    skipped when Auth0 has no session to refresh from on this origin (see
 *    scheduleProactiveRefresh), since a handoff-landed origin has nothing
 *    to silently re-authenticate against; it simply gets a wholesale new
 *    JWT on the next explicit switch/reload instead.
 *
 * Works in both standalone (Auth0Provider) and embedded (ShellAuthProvider)
 * modes because it reads the unified isAuthenticated from useAuth() to
 * decide whether the exchange is even needed, alongside the raw Auth0 SDK
 * state (useAuth0()) to decide whether the exchange is actually runnable.
 */
const Auth0AuthBridge = ({ children }) => {
  // Runs exactly once, synchronously, during this component's first render —
  // before useAuth() below and before any descendant (e.g. ProtectedRoute)
  // evaluates its own on this same render pass. A useEffect would run too
  // late (after the initial commit), letting ProtectedRoute bounce to
  // /login for one frame before the handoff's token is even visible.
  // consumeHandoff() is idempotent, so re-invocation (StrictMode dev double-
  // render) is harmless.
  useState(consumeHandoff);

  const appAuth = useAppAuthSafe();
  const auth0 = useAuth0();
  const { isAuthenticated, getAccessTokenSilently } = useAuth();
  const getGlobalInformation = appAuth.getGlobalInformation;
  const clear = appAuth.clear;
  const refreshTimerRef = useRef(null);
  const retryTimerRef = useRef(null);
  /** Set on unmount so a retry that is mid-sleep stops instead of resuming. */
  const cancelledRef = useRef(false);
  // Tracks the auth0 user.sub we've already exchanged for a backend JWT.
  // Used (instead of the blended `isAuthenticated` from useAuth()) to decide
  // whether the exchange still needs to run — `isAuthenticated` also turns
  // true the instant Auth0 itself authenticates (see useAuth.js's fallback
  // clause), i.e. BEFORE this exchange even starts, so using it as the
  // "already done" signal skipped runAuth0Flow entirely and left the app
  // stuck on "Authenticating…" forever (only Auth0's own /oauth/token call
  // ever fired; our /v1/token never did). A ref keyed to the exchanged
  // identity still prevents the infinite loop a successful setToken() would
  // otherwise cause (it re-renders this component too, since isAuthenticated
  // is reactive to the token), without that false-positive.
  const exchangedSubRef = useRef(null);

  /**
   * Renew the backend JWT now.
   *
   * Goes through `tokenService.refreshToken()` rather than calling
   * `getAccessTokenSilently` + `fetchTokenWithAuth0` directly, because that
   * function owns the in-flight queue: a 401 retry and this renewal firing
   * at the same moment then share one `/v1/token` round trip instead of
   * racing and leaving whichever finishes second as the stored token.
   *
   * A failure only ends the session when Auth0 says the session is actually
   * gone. Anything else gets a bounded retry — the previous version logged
   * the user out on any error at all, which is how a dropped packet became
   * a full-page trip through Auth0 and back.
   *
   * The retries are a loop rather than a self-scheduling callback so the
   * function does not have to reference itself, and `cancelledRef` is what
   * stops a retry that is still sleeping when the bridge unmounts.
   */
  const renewToken = useCallback(async () => {
    // FIX: on a handoff subdomain Auth0 has no session on this origin, but the
    // backend JWT is renewable from its own claims (see tokenService.refreshToken).
    // Bail only when there is genuinely nothing to renew from.
    const canRenew =
      auth0.isAuthenticated || tokenService.getTokenExpiresInMs() != null;
    if (!canRenew) return;

    for (let attempt = 0; ; attempt += 1) {
      try {
        await tokenService.refreshToken();
        return;
      } catch (err) {
        if (isSessionGone(err)) {
          console.error('[Auth0AuthBridge] Session is gone, signing out:', err);
          clearAuthAndRedirect();
          return;
        }

        const delay = RENEW_RETRY_DELAYS_MS[attempt];
        if (delay == null) {
          console.error('[Auth0AuthBridge] Token renewal gave up after retries:', err);
          return;
        }

        console.warn(`[Auth0AuthBridge] Token renewal failed, retrying in ${delay}ms:`, err);
        await new Promise((resolve) => {
          retryTimerRef.current = setTimeout(resolve, delay);
        });
        if (cancelledRef.current) return;
      }
    }
  }, [auth0.isAuthenticated]);

  const scheduleProactiveRefresh = useCallback(() => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }

    // Renew whenever there is a token to renew from. On a cross-subdomain
    // handoff origin Auth0 has no session here, but the backend JWT is
    // renewable from its own claims (see tokenService.refreshToken), so the
    // exp check below is the real gate: no token / no exp means nothing to do.
    // (Previously this bailed on !auth0.isAuthenticated, which left the token
    // on a workspace subdomain to expire at 15 min and froze the chat.)
    const expiresInMs = tokenService.getTokenExpiresInMs();
    if (expiresInMs == null) return;

    const refreshInMs = Math.max(expiresInMs - REFRESH_BUFFER_MS, 0);
    refreshTimerRef.current = setTimeout(renewToken, refreshInMs);
  }, [auth0.isAuthenticated, renewToken]);

  /**
   * Keeps the backend JWT alive across the three things that happen to a
   * long-lived tab.
   *
   * **Mount and dependency changes.** `scheduleProactiveRefresh()` is called
   * here, not only from the token-change listener. It used to be armed
   * *only* by that listener, so this effect's own cleanup — which clears the
   * timer — disarmed the renewal permanently whenever the effect re-ran
   * without a `setToken()` following it. The token then simply expired.
   *
   * **Idle.** A thirteen-minute `setTimeout` does not survive a tab the
   * browser has throttled: Chrome runs timers in a hidden tab at most once a
   * minute after five minutes, and not at all while the machine sleeps. The
   * timer fires late by however long the tab was away, which is exactly the
   * reported symptom — leave the app sitting, come back, and the first thing
   * you click runs on a dead token. So renewal also hangs off the events
   * that coincide with *coming back*: the page becoming visible, the window
   * regaining focus, the network returning. Inside the buffer it renews on
   * the spot instead of trusting a deadline that has already slipped.
   *
   * **Suspend.** Re-arming on regain re-reads the real remaining lifetime,
   * so a timer scheduled before a suspend is replaced rather than believed.
   */
  useEffect(() => {
    cancelledRef.current = false;
    scheduleProactiveRefresh();

    const unsubscribe = tokenService.onTokenChange(scheduleProactiveRefresh);

    const renewIfDue = () => {
      const expiresInMs = tokenService.getTokenExpiresInMs();
      if (expiresInMs == null) return;

      if (expiresInMs <= REFRESH_BUFFER_MS) renewToken();
      else scheduleProactiveRefresh();
    };

    // `visibilitychange` fires on the way out as well as the way back, and
    // only the way back is interesting. `focus` and `online` are not gated
    // on visibility: focus implies the page is visible, and a network that
    // just came back is worth acting on either way.
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') renewIfDue();
    };

    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('focus', renewIfDue);
    window.addEventListener('online', renewIfDue);

    return () => {
      cancelledRef.current = true;
      unsubscribe();
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('focus', renewIfDue);
      window.removeEventListener('online', renewIfDue);
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    };
  }, [renewToken, scheduleProactiveRefresh]);

  useEffect(() => {
    if (auth0.isLoading) return;

    if (!auth0.isAuthenticated || !auth0.user) {
      tokenService.setAuth0TokenGetter(null);
      // Landed via a cross-subdomain handoff with no Auth0 session on this
      // origin at all (see tokenHandoff.js — Auth0 was only ever invoked on
      // the ORIGIN where the identity login happened) is expected, not an
      // error — `isAuthenticated` is still true there via our own valid
      // backend token, so don't wipe it.
      if (!isAuthenticated) clear();
      return;
    }

    tokenService.setAuth0TokenGetter(getAccessTokenSilently);

    // Already exchanged a backend JWT for this exact Auth0 identity — skip
    // to avoid re-running on every re-render this component gets once the
    // token is set (see exchangedSubRef's own comment above for why this,
    // and not the blended `isAuthenticated`, is the right guard here).
    if (exchangedSubRef.current === auth0.user.sub) return;

    const runAuth0Flow = async () => {
      try {
        const auth0Token = await getAccessTokenSilently();
        if (!auth0Token) {
          console.error('[Auth0AuthBridge] No access token');
          clear();
          return;
        }
        await getGlobalInformation({ auth0AccessToken: auth0Token });
        exchangedSubRef.current = auth0.user.sub;
      } catch (err) {
        // Only a genuinely dead Auth0 session justifies signing the user
        // out — this used to fire on *any* error, so a dropped request
        // during the first exchange wiped the session and hard-redirected
        // through Auth0. Everything else leaves `exchangedSubRef` unset, so
        // the next regain event (visible / focus / online) retries.
        if (isSessionGone(err)) {
          console.error('[Auth0AuthBridge] Session is gone, signing out:', err);
          clearAuthAndRedirect();
          return;
        }
        console.error('[Auth0AuthBridge] Token exchange failed, will retry:', err);
      }
    };

    runAuth0Flow();
  }, [
    auth0.isLoading,
    auth0.isAuthenticated,
    auth0.user,
    isAuthenticated,
    getGlobalInformation,
    clear,
    getAccessTokenSilently,
  ]);

  return children;
};

export default Auth0AuthBridge;
